import { USER_GROUPS } from '../constants';
import { IMPERSONATION_VIEW } from '../constants/impersonation';
import { UserType } from '../store/slices/authSlice';

export const hasPermissionOrAdmin = (user: UserType | null, permissions: string[]) => {
  return (
    !!user &&
    (user.isSuperuser || user.isStaff || user.all_permissions?.some((permission) => permissions.includes(permission)))
  );
};

export const canAccessSalesBattlecardContent = (user: UserType | null) => {
  return Boolean(user?.feature_flags?.show_sales_battlecard_content);
};

// Admin (superuser) supporting this user, regardless of the selected view
export const isSupportingUser = (user: UserType | null) => Boolean(user?.manager?.is_superuser);

// Admin supporting this user in Admin View - grants admin-only visibility. False in "View as supported user" mode.
export const isManagedBySuperuser = (user: UserType | null) =>
  isSupportingUser(user) && user?.current_view === IMPERSONATION_VIEW.ADMIN;

export const canAccessAICompetitiveIntelligence = (user: UserType | null) =>
  Boolean(user?.feature_flags?.show_ai_competitive_intelligence);

export const hasCompanyAIOverviewFlag = (user: UserType | null) =>
  Boolean(user?.feature_flags?.show_ai_company_overview);

export const canAccessCompanyAIOverview = (user: UserType | null, communitiesCount: number) =>
  hasCompanyAIOverviewFlag(user) &&
  canAccessAICompetitiveIntelligence(user) &&
  !user?.isSuperuser &&
  communitiesCount > 1;

export const hasCmiDigestFlag = (user: UserType | null) => Boolean(user?.feature_flags?.show_cmi_digest);

export const canAccessActionPlans = (user: UserType | null) =>
  Boolean(user?.feature_flags?.show_action_plans || isManagedBySuperuser(user));

export const canAccessActionPlansCompanyView = (user: UserType | null) =>
  Boolean(
    (user?.feature_flags?.show_action_plans && user?.feature_flags?.show_action_plans_company) ||
    isManagedBySuperuser(user) ||
    user?.isSuperuser ||
    user?.isStaff
  );

export const canRegenerateAICI = (user: UserType | null) =>
  Boolean(user?.isSuperuser || user?.isStaff || isManagedBySuperuser(user));

export const canAccessAISpeedToLead = (user: UserType | null) => {
  return Boolean(user?.feature_flags?.show_ai_speed_to_lead || isManagedBySuperuser(user));
};

// The literal flag only, without the impersonating-superuser bypass in canAccessAISpeedToLead. Used to decide
// whether to hide the Speed to Lead Coming Soon card based on the account's own flag state.
export const hasAISpeedToLeadFlag = (user: UserType | null) => Boolean(user?.feature_flags?.show_ai_speed_to_lead);

export const canAccessComingSoonFeatures = (user: UserType | null) =>
  Boolean(user?.feature_flags?.show_coming_soon_features);

export const canViewPricingData = (user: UserType | null) => {
  return Boolean(user?.feature_flags?.view_pricing_data && !user?.isSuperuser);
};

export const isIncentivesCorporate = (user: UserType | null) => {
  return Boolean(user?.groups.includes(USER_GROUPS.INCENTIVES_CORPORATE));
};

export const isIncentivesSalesperson = (user: UserType | null) => {
  return Boolean(user?.groups.includes(USER_GROUPS.INCENTIVES_SALESPERSON));
};

export const isMysteryShopAdmin = (user: UserType | null) => {
  return Boolean(user?.groups.includes(USER_GROUPS.MYSTERY_SHOP_ADMIN));
};

export const isMysteryShopShopper = (user: UserType | null) => {
  return Boolean(user?.groups.includes(USER_GROUPS.MYSTERY_SHOP_SHOPPER));
};

export const isVendorShopAdmin = (user: UserType | null) => {
  return Boolean(user?.groups.includes(USER_GROUPS.VENDOR_SHOP_ADMIN));
};

export const canViewShopperCommunication = (user: UserType | null) => {
  return Boolean(user?.feature_flags?.show_shopper_communication);
};

export const canManageShopperEmails = (user: UserType | null) => {
  return Boolean(
    user &&
    user.feature_flags?.show_shopper_communication &&
    (user.isSuperuser || user.isStaff || isMysteryShopAdmin(user) || isMysteryShopShopper(user))
  );
};

export const canViewRingCentralRecords = (user: UserType | null) => {
  return Boolean(
    user &&
    user.feature_flags?.show_ringcentral_records &&
    (user.isSuperuser || user.isStaff || isMysteryShopAdmin(user) || isVendorShopAdmin(user))
  );
};

export const canViewActionCenter = (user: UserType | null) => {
  return Boolean(user?.feature_flags?.show_action_center);
};
